'use client';

import { useId, useState, type ChangeEvent } from 'react';
import {
  APPOINTMENT_TEXTS,
  bookingBody,
  EMPTY_CONTACT,
  knownQuoteToken,
  missingContactFields,
  postBooking,
  unavailableCallbackOf,
  type ContactFields,
  type UnavailableCallback,
  type UnavailableWithCallback,
} from '@/app/lib/guideChat/appointment';
import { CHAT_LABELS } from '@/app/lib/guideChat/labels';
import type { UnavailableResponse } from '@/app/lib/guideChat/types';
import { APPOINTMENT_URL, FAQ_URL } from './api';

// `links` is the plain notice when no form is offered; `failed` falls back to the same links after a refused callback.
export type NoticeState = 'form' | 'sending' | 'done' | 'failed' | 'links';

interface NoticeProps {
  info: UnavailableResponse;
  conversationId?: string | null;
  question?: string | null;
}

export default function UnavailableNotice({ info, conversationId, question }: NoticeProps) {
  const callback = unavailableCallbackOf(info as UnavailableWithCallback, { conversationId, question });
  const [state, setState] = useState<NoticeState>(callback ? 'form' : 'links');
  const [contact, setContact] = useState<ContactFields>(EMPTY_CONTACT);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  async function submit() {
    if (!callback || state === 'sending') return;
    if (missingContactFields(contact).length > 0) {
      setError(APPOINTMENT_TEXTS.missingContact);
      return;
    }
    setError(null);
    setState('sending');
    const result = await postBooking(
      bookingBody({
        mode: 'callback',
        contact,
        product: callback.product,
        handover: callback.handover,
        quoteToken: knownQuoteToken(),
      }),
    );
    if (result.ok) {
      setConfirmation(result.confirmation ?? callback.confirmation);
      setState('done');
      return;
    }
    setState('failed');
  }

  return (
    <UnavailableNoticeView
      info={info}
      callback={callback}
      state={state}
      contact={contact}
      error={error}
      confirmation={confirmation}
      onChange={(field, value) => setContact((c) => ({ ...c, [field]: value }))}
      onSubmit={submit}
    />
  );
}

interface ViewProps {
  info: UnavailableResponse;
  callback: UnavailableCallback | null;
  state: NoticeState;
  contact: ContactFields;
  error?: string | null;
  confirmation?: string | null;
  onChange?: (field: keyof ContactFields, value: string) => void;
  onSubmit?: () => void;
}

const inputCls =
  'w-full rounded border border-amber-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400';

const FIELDS: { field: keyof ContactFields; type: string; autoComplete: string }[] = [
  { field: 'firstName', type: 'text', autoComplete: 'given-name' },
  { field: 'lastName', type: 'text', autoComplete: 'family-name' },
  { field: 'phone', type: 'tel', autoComplete: 'tel' },
  { field: 'email', type: 'email', autoComplete: 'email' },
];

export function UnavailableNoticeView({ info, callback, state, contact, error, confirmation, onChange, onSubmit }: ViewProps) {
  const id = useId();
  const showForm = callback !== null && (state === 'form' || state === 'sending');

  return (
    <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <p className="font-semibold">{info.message}</p>

      {state === 'done' && (
        <p className="mt-1 text-xs" role="status">
          {confirmation ?? callback?.confirmation ?? APPOINTMENT_TEXTS.callbackConfirmed}
        </p>
      )}

      {showForm && (
        <form
          className="mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.();
          }}
        >
          <div className="grid grid-cols-2 gap-1.5">
            {FIELDS.map(({ field, type, autoComplete }) => (
              <div key={field}>
                <label htmlFor={`${id}-${field}`} className="block text-[11px] text-amber-800">
                  {APPOINTMENT_TEXTS[field]}
                </label>
                <input
                  id={`${id}-${field}`}
                  name={field}
                  type={type}
                  autoComplete={autoComplete}
                  value={contact[field]}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.(field, e.target.value)}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
          {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={state === 'sending'}
            className="mt-2 w-full rounded-lg bg-amber-600 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {state === 'sending' ? APPOINTMENT_TEXTS.sending : callback.submitLabel}
          </button>
        </form>
      )}

      {(state === 'failed' || state === 'links') && (
        <>
          {state === 'failed' && <p className="mt-1 text-xs">{APPOINTMENT_TEXTS.callbackFailed}</p>}
          <p className="mt-1 text-xs">{CHAT_LABELS.unavailableIntro}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
            <li>
              <a href={info.faqUrl ?? FAQ_URL} className="font-semibold underline" target="_blank" rel="noopener noreferrer">
                {CHAT_LABELS.unavailableGuideLink}
              </a>
            </li>
            <li>
              <a href={info.appointmentUrl ?? APPOINTMENT_URL} className="font-semibold underline">
                {CHAT_LABELS.unavailableAppointmentLink}
              </a>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
